// @ts-check
// Conectores y ramificaciones entre bloques del editor de diagramas (TSK-0025).
//
// Objetivo (docs/01-roadmap.md §7, §23; entradas de la tarea): permitir crear
// y eliminar conexiones entre bloques y definir ramas de decisión
// (verdadero/falso) y cuerpos de bucle. Punto crítico de arquitectura (mismo
// que documenta el propio fichero de tarea): la conexión visual/de la UI
// MAPEA a estructura lógica de la IR (orden dentro de `Secuencia`, ramas de
// `Condicional`, cuerpo de `BucleMientras`/`BucleFinPara`/`RepetirHasta`),
// nunca al revés. Este módulo no introduce ningún nodo IR nuevo: reutiliza
// `Secuencia` como el contenedor de conexión ya existente (TSK-0021,
// `editor-model.js`) tanto para la secuencia raíz como para `rama_si`,
// `rama_sino` y `cuerpo`, todas ellas nodos `Secuencia` con `id` propio.
//
// Diseño de accesibilidad (docs/03-frontend.md §13.1, WCAG 2.2 AA; reglas de
// diseño de la tarea):
// 1. Deliberadamente NO se implementa arrastre personalizado de conectores.
//    Conectar/desconectar/reordenar se ofrecen con controles nativos
//    (`<select>`, `<button>`), que ya son 100 % operables con teclado sin
//    reimplementar nada: esto evita el riesgo de un flujo "solo puntero" y
//    resuelve el criterio de aceptación 1 por construcción, en vez de añadir
//    manejadores de teclado a medida (que si se hicieran mal podrían dejar de
//    cubrir algún caso).
// 2. La estructura de ramificación se expresa con AGRUPACIÓN SEMÁNTICA en el
//    DOM (listas anidadas `<ol>` dentro de `<section aria-label="...">` por
//    cada rama/cuerpo), nunca por posición x/y: un lector de pantalla puede
//    recorrer "Condicional X > rama verdadero > ..." sin ver la pantalla.
//    Este módulo NO usa `element.style.left/top` en ningún punto (a
//    diferencia de `canvas-view.js`): no aplica ADR-0004 porque no coloca
//    nada libremente en 2D, así que no se amplía ese ADR ni se necesita uno
//    nuevo para este módulo.
// 3. "Conexión inválida hacia Inicio" (criterio de aceptación) se modela con
//    dos pseudo-anclas fijas, `ID_CONTENEDOR_INICIO`/`ID_CONTENEDOR_FIN`
//    (mismos ids que ya usa `palette-view.js` para sus bloques "Inicio"/"Fin"
//    sin nodo IR, documentado de forma redundante e intencional igual que el
//    contrato MIME entre `palette-view.js`/`canvas-view.js`): Inicio no
//    admite conexiones entrantes (no tiene predecesor) y Fin no admite
//    conexiones salientes de bloques nuevos (no tiene sucesor). Se ofrecen
//    como opciones seleccionables en el propio `<select>` de destino para que
//    el rechazo sea alcanzable y comprensible por teclado y no un caso oculto.
//
// Dirección de dependencias (docs/03-frontend.md §4.2): este módulo importa
// SOLO `editor-model.js` (dato + funciones puras, ya existente). No importa
// `canvas-view.js`, `editor-visual-state.js` ni `services/*`; no llama
// `fetch`. Al igual que documenta `canvas-view.js` sobre `block-operations.js`
// (TSK-0024, todavía no implementado), este componente se monta sobre su
// propio `elemento` para que una futura vista de página lo combine con el
// canvas; esa composición de página queda fuera de las rutas permitidas de
// esta tarea.
//
// Ambigüedad NO resuelta en silencio (repórtese también en la respuesta
// final del agente ejecutor): no existe todavía ningún "pool de bloques sin
// conectar" compartido con `canvas-view.js`/`palette-view.js` (cada uno de
// esos módulos coloca un nodo directamente en la Secuencia raíz al
// arrastrarlo o activarlo, TSK-0022/TSK-0023). Este módulo mantiene su PROPIA
// lista de "bloques sin conectar" en memoria (parámetro `bloquesSinConectar`
// y método `agregarBloqueSinConectar`), pensada para que una futura página
// que combine paleta + canvas + conectores decida cómo alimentarla (p. ej.
// enviando ahí los nodos que la paleta cree en vez de colocarlos siempre en
// el canvas). Esa integración de extremo a extremo no está resuelta por esta
// tarea y se dela explícitamente pendiente.
//
// --------------------------------------------------------------------------
// TSK-0196 (DEC-069 fase 3, docs/generated/decisions.md#DEC-069, "reconciliar
// el rol de connectors.js"): ROL ACTUAL de este módulo, ya que el grafo de
// arcos (`arrow-connection-graph.js`/`arrow-graph-compiler.js`, TSK-0191/
// TSK-0193) es desde TSK-0195 la AUTORIDAD de conectividad VISIBLE del
// editor (qué flecha conecta con qué, arrastrable/reconectable/borrable en
// `canvas-view.js`).
// --------------------------------------------------------------------------
// Este módulo YA NO gobierna esa conectividad visible: ninguna de sus
// funciones participa en "qué flecha se ve entre qué bloques" -- eso lo
// deciden `grafoInteractivo` (estado propio de `canvas-view.js`, mutado por
// `agregarArco`/`reconectarExtremo`/`eliminarArco` de
// `arrow-connection-graph.js`) y, en el momento de Ejecutar/Corrección/
// persistencia, `compilarGrafoAIr` (`arrow-graph-compiler.js`, TSK-0193). El
// rol de `connectors.js` se REDUCE, de forma coherente con ese cambio de
// autoridad, a exactamente lo que sigue usando `canvas-view.js` -- verificado
// con `grep` antes de tocar este fichero, no supuesto:
//
//   - `conectarBloque`/`desconectarBloque`/`moverBloqueEnContenedor`: las
//     ÚNICAS 3 operaciones ESTRUCTURALES puras sobre la IR (`Programa`) que
//     `canvas-view.js` sigue consumiendo, SIN reimplementarlas (docs/03-
//     frontend.md §4.2), para exactamente 3 acciones que NO son "dibujar una
//     flecha": colocar un bloque nuevo desde la paleta (`colocarEnContenedor`
//     → `conectarBloque`), eliminarlo (`eliminarBloque`/`deshacerEliminacion`
//     → `desconectarBloque`/`conectarBloque`) y el reordenado POR TECLADO de
//     un bloque ya colocado dentro de su propio contenedor
//     (`reordenarBloque` → `moverBloqueEnContenedor`) -- las 3 siguen siendo
//     mutaciones de la ESTRUCTURA (el árbol `Secuencia`/`rama_si`/
//     `rama_sino`/`cuerpo`), no de la capa de flechas interactiva (que tiene
//     su PROPIO estado, `grafoInteractivo`, re-derivado de esa misma
//     estructura en cada `renderizar()`, ver el comentario de cabecera
//     "TSK-0194"/"TSK-0195" de `canvas-view.js`). Siguen siendo la vía
//     CORRECTA y ÚNICA para esas 3 acciones: no se retiran ni se marcan como
//     obsoletas.
//   - `reemplazarNodoPorId`: función interna que usan las 3 anteriores
//     (sustituir, de forma inmutable, un nodo por su `id` en cualquier punto
//     del árbol) -- sigue siendo necesaria mientras ellas lo sean.
//
// Lo que este módulo mantiene pero que YA NO usa ningún otro archivo del
// editor (verificado con `grep -rn` sobre `src/`: sin resultados fuera de
// este propio fichero y de sus tests dedicados) -- **NO se retira en esta
// ficha**, decisión de alcance explícita, no silenciosa:
//   - `crearVistaConectores` (+ su UI completa, "Componente de UI" más abajo)
//     es un panel STANDALONE de TSK-0025 que `editor-controller.js` dejó de
//     montar hace varias tareas ("DECISIÓN 5" en ese archivo, documentado
//     allí, ANTERIOR a esta ficha y ajeno a DEC-069): ya era código sin
//     consumidor de producción antes de que existiera el grafo de arcos, así
//     que su desuso no es consecuencia de esta tarea. NO se retira aquí
//     porque (a) el encargo de esta ficha nombra explícitamente solo
//     `conectarBloque`/`desconectarBloque`/`moverBloqueEnContenedor`/
//     `reemplazarNodoPorId` como el objeto de la reconciliación, nunca este
//     panel; (b) tiene su PROPIA suite de tests dedicada y ya aprobada
//     (`tests/unit/diagram-editor/connectors.spec.js` +
//     `tests/accessibility/diagram-editor/connectors.spec.js`) que ejercita
//     precisamente esta UI -- retirarla invalidaría esos tests, una
//     ampliación de alcance no pedida por el encargo ("prioriza CERO
//     regresión... sobre el alcance"). `listarContenedores`/
//     `ID_CONTENEDOR_INICIO`/`ID_CONTENEDOR_FIN`/`validarContenedorDestino`/
//     `ConexionInvalidaError` son soporte de ESE panel (enumerar
//     contenedores para su `<select>`, validar su destino, el error que
//     lanza `conectarBloque` al violarlo): quedan documentados aquí como
//     "en desuso fuera de este módulo" para que una tarea futura que sí
//     quiera retirar el panel entero sepa qué se lleva con él.
//
// Ningún nodo IR nuevo, ninguna importación nueva, ningún cambio de
// comportamiento en este módulo por esta ficha: es EXCLUSIVAMENTE
// documentación de rol, tal como pide el criterio de aceptación 2 ("el rol
// de connectors.js queda documentado o reducido coherentemente").

import {
  esNodo,
  TIPO_NODO,
  crearPrograma,
  buscarNodoPorId,
  buscarSecuenciaPadre,
  insertarEnSecuencia,
  eliminarDeSecuencia,
  moverEnSecuencia,
} from './editor-model.js';

/**
 * @typedef {import('./editor-model.js').NodoModelo} NodoModelo
 */

/** Pseudo-ancla "Inicio": mismo id que usa `palette-view.js` para su bloque
 * "Inicio" (que tampoco tiene nodo IR propio). No es un `id` de ningún nodo
 * real del modelo; es un valor reservado reconocido únicamente por
 * `validarContenedorDestino` para dar un mensaje de rechazo comprensible. */
export const ID_CONTENEDOR_INICIO = 'inicio';
/** Pseudo-ancla "Fin": ver `ID_CONTENEDOR_INICIO`. */
export const ID_CONTENEDOR_FIN = 'fin';

/** Nodos de bucle: todos exponen su cuerpo en el campo `cuerpo` (mismo
 * nombre de campo que sus dataclasses equivalentes en ir.py). Tipado como
 * `Set<string>` (no como la unión literal que TypeScript infiere de los 3
 * valores) porque se consulta con `sentencia.nodo`, que es `string`. */
const NODOS_BUCLE = /** @type {Set<string>} */ (
  new Set([TIPO_NODO.BUCLE_MIENTRAS, TIPO_NODO.BUCLE_FIN_PARA, TIPO_NODO.REPETIR_HASTA])
);

/**
 * Error específico para un intento de conexión que viola una regla de
 * estructura del diagrama (p.ej. hacia "Inicio"), distinguible en `catch` de
 * un error de programación (`TypeError`) por quien consuma este módulo.
 */
export class ConexionInvalidaError extends Error {
  /** @param {string} mensaje */
  constructor(mensaje) {
    super(mensaje);
    this.name = 'ConexionInvalidaError';
  }
}

/**
 * @param {unknown} valor
 * @returns {NodoModelo}
 */
function requerirPrograma(valor) {
  if (!esNodo(valor) || valor.nodo !== TIPO_NODO.PROGRAMA) {
    throw new TypeError('se requiere un nodo Programa del modelo del editor');
  }
  const cuerpo = /** @type {Record<string, unknown>} */ (valor).cuerpo;
  if (!esNodo(cuerpo) || cuerpo.nodo !== TIPO_NODO.SECUENCIA) {
    throw new TypeError('el "programa" recibido debe tener un "cuerpo" de tipo Secuencia');
  }
  return valor;
}

// --------------------------------------------------------------------------
// Reglas puras de conexión/estructura (testeables sin DOM)
// --------------------------------------------------------------------------

/**
 * Comprueba si `contenedorId` es un destino de conexión válido.
 * Devuelve un mensaje de error comprensible si NO lo es, o `null` si es
 * válido (no comprueba aquí que exista de verdad en el árbol: eso lo hace
 * `conectarBloque`, que sí tiene el `programa` para buscarlo).
 * @param {string} contenedorId
 * @returns {string | null}
 */
export function validarContenedorDestino(contenedorId) {
  if (contenedorId === ID_CONTENEDOR_INICIO) {
    return 'No se puede conectar hacia "Inicio": Inicio no admite conexiones entrantes.';
  }
  if (contenedorId === ID_CONTENEDOR_FIN) {
    return 'No se puede conectar hacia "Fin": Fin no admite conexiones salientes de bloques nuevos.';
  }
  return null;
}

/**
 * Reconstruye `raiz` sustituyendo, de forma inmutable, el nodo cuyo `id`
 * coincida con `id` por `nuevoNodo`, dondequiera que esté anidado (secuencia
 * raíz, `rama_si`/`rama_sino` de un `Condicional`, `cuerpo` de un bucle...).
 * Mismo patrón de recorrido que `clonarConNuevosIds` de `editor-model.js`
 * (no se importa de allí: es una operación nueva -sustituir, no clonar ids-
 * específica de este módulo, y `editor-model.js` está en rutas prohibidas
 * para edición).
 * @param {NodoModelo} raiz
 * @param {string} id
 * @param {NodoModelo} nuevoNodo
 * @returns {NodoModelo}
 */
export function reemplazarNodoPorId(raiz, id, nuevoNodo) {
  if (!esNodo(raiz)) {
    throw new TypeError('"raiz" debe ser un NodoModelo válido');
  }
  if (raiz.id === id) return nuevoNodo;
  /** @type {Record<string, unknown>} */
  const clon = { ...raiz };
  for (const [clave, valor] of Object.entries(raiz)) {
    if (esNodo(valor)) {
      clon[clave] = reemplazarNodoPorId(valor, id, nuevoNodo);
    } else if (Array.isArray(valor)) {
      clon[clave] = valor.map((elemento) =>
        esNodo(elemento) ? reemplazarNodoPorId(elemento, id, nuevoNodo) : elemento
      );
    }
  }
  return /** @type {NodoModelo} */ (clon);
}

/**
 * Enumera todos los contenedores de conexión (nodos `Secuencia`) presentes
 * en `programa`: la secuencia raíz, las ramas de cada `Condicional` y el
 * cuerpo de cada bucle, con una etiqueta legible que expresa su posición
 * estructural (para el `<select>` de destino; no depende de ninguna
 * coordenada visual).
 * @param {NodoModelo} programa
 * @returns {{ id: string, etiqueta: string }[]}
 */
export function listarContenedores(programa) {
  requerirPrograma(programa);
  /** @type {{ id: string, etiqueta: string }[]} */
  const contenedores = [];

  /**
   * @param {NodoModelo} secuencia
   * @param {string} etiqueta
   */
  function recorrer(secuencia, etiqueta) {
    contenedores.push({ id: secuencia.id, etiqueta });
    const sentencias = /** @type {NodoModelo[]} */ (secuencia.sentencias);
    for (const sentencia of sentencias) {
      if (sentencia.nodo === TIPO_NODO.CONDICIONAL) {
        const ramaSi = /** @type {NodoModelo} */ (sentencia.rama_si);
        recorrer(ramaSi, `${etiqueta} → Condicional ${sentencia.id} (rama verdadero)`);
        const ramaSino = /** @type {NodoModelo | null} */ (sentencia.rama_sino);
        if (ramaSino) {
          recorrer(ramaSino, `${etiqueta} → Condicional ${sentencia.id} (rama falso)`);
        }
      } else if (NODOS_BUCLE.has(/** @type {string} */ (sentencia.nodo))) {
        const cuerpo = /** @type {NodoModelo} */ (sentencia.cuerpo);
        recorrer(cuerpo, `${etiqueta} → Bucle ${sentencia.id} (cuerpo)`);
      }
    }
  }

  recorrer(/** @type {NodoModelo} */ (programa.cuerpo), 'Secuencia principal');
  return contenedores;
}

/**
 * Conecta (inserta) `nodo` en el contenedor identificado por `contenedorId`.
 * Es la operación lógica detrás de "crear una conexión"/"crear una rama":
 * el contenedor puede ser la secuencia raíz o cualquier `rama_si`/
 * `rama_sino`/`cuerpo` ya existente en el árbol (todas son nodos `Secuencia`
 * con `id` propio).
 * @param {NodoModelo} programa
 * @param {{ contenedorId: string, nodo: NodoModelo, indice?: number }} opciones
 * @returns {NodoModelo} el nuevo `programa` (no se muta el recibido)
 */
export function conectarBloque(programa, { contenedorId, nodo, indice }) {
  requerirPrograma(programa);

  const mensajeInvalido = validarContenedorDestino(contenedorId);
  if (mensajeInvalido) {
    throw new ConexionInvalidaError(mensajeInvalido);
  }

  if (!esNodo(nodo)) {
    throw new TypeError('"nodo" debe ser un NodoModelo válido');
  }

  if (buscarNodoPorId(programa, nodo.id)) {
    throw new ConexionInvalidaError(
      `el bloque '${nodo.id}' ya está conectado en el programa; desconéctalo antes de volver a conectarlo`
    );
  }

  const contenedor = buscarNodoPorId(programa, contenedorId);
  if (!contenedor || contenedor.nodo !== TIPO_NODO.SECUENCIA) {
    throw new TypeError(`'${contenedorId}' no identifica una Secuencia existente en el programa`);
  }

  const nuevaSecuencia = insertarEnSecuencia(contenedor, nodo, indice);
  return reemplazarNodoPorId(programa, contenedorId, nuevaSecuencia);
}

/**
 * Desconecta el bloque `id`, dondequiera que esté conectado (secuencia raíz
 * o cualquier rama/cuerpo anidado), y lo devuelve junto al nuevo `programa`
 * para que el llamador decida qué hacer con él (p.ej. añadirlo a un pool de
 * "bloques sin conectar").
 * @param {NodoModelo} programa
 * @param {string} id
 * @returns {{ programa: NodoModelo, nodo: NodoModelo }}
 */
export function desconectarBloque(programa, id) {
  requerirPrograma(programa);

  const contenedor = buscarSecuenciaPadre(programa, id);
  if (!contenedor) {
    throw new TypeError(`el bloque '${id}' no está conectado en ninguna secuencia del programa`);
  }
  const nodo = /** @type {NodoModelo} */ (buscarNodoPorId(programa, id));
  const nuevaSecuencia = eliminarDeSecuencia(contenedor, id);
  const nuevoPrograma = reemplazarNodoPorId(programa, contenedor.id, nuevaSecuencia);
  return { programa: nuevoPrograma, nodo };
}

/**
 * Reordena el bloque `id` dentro del contenedor `contenedorId` (mueve la
 * conexión, no cambia de rama/secuencia).
 * @param {NodoModelo} programa
 * @param {string} contenedorId
 * @param {string} id
 * @param {number} nuevoIndice
 * @returns {NodoModelo}
 */
export function moverBloqueEnContenedor(programa, contenedorId, id, nuevoIndice) {
  requerirPrograma(programa);
  const contenedor = buscarNodoPorId(programa, contenedorId);
  if (!contenedor || contenedor.nodo !== TIPO_NODO.SECUENCIA) {
    throw new TypeError(`'${contenedorId}' no identifica una Secuencia existente en el programa`);
  }
  const nuevaSecuencia = moverEnSecuencia(contenedor, id, nuevoIndice);
  return reemplazarNodoPorId(programa, contenedorId, nuevaSecuencia);
}

// --------------------------------------------------------------------------
// Componente de UI
// --------------------------------------------------------------------------

/** Contador de módulo para ids de encabezado únicos por instancia (mismo
 * patrón que `canvas-view.js`/`palette-view.js`: evita `landmark-unique` con
 * varias instancias montadas a la vez). */
let contadorInstancias = 0;

/**
 * @typedef {{ nodo: NodoModelo, etiqueta: string }} BloqueSinConectar
 */

/**
 * Crea el panel de conectores/ramas.
 * @param {{
 *   programa?: NodoModelo,
 *   bloquesSinConectar?: BloqueSinConectar[],
 *   onCambioPrograma?: (programa: NodoModelo) => void,
 * }} [opciones]
 * @returns {{
 *   elemento: HTMLElement,
 *   obtenerPrograma: () => NodoModelo,
 *   obtenerBloquesSinConectar: () => BloqueSinConectar[],
 *   agregarBloqueSinConectar: (nodo: NodoModelo, etiqueta?: string) => void,
 *   destruir: () => void,
 * }}
 */
export function crearVistaConectores(opciones = {}) {
  let programaActual = requerirPrograma(opciones.programa ?? crearPrograma());
  /** @type {BloqueSinConectar[]} */
  let bloquesSinConectar = Array.isArray(opciones.bloquesSinConectar)
    ? opciones.bloquesSinConectar.slice()
    : [];
  const notificarCambioPrograma =
    typeof opciones.onCambioPrograma === 'function' ? opciones.onCambioPrograma : null;

  /** Etiquetas de presentación por id, igual que en `canvas-view.js`: dato
   * SOLO de esta vista, ni el modelo lógico ni la capa visual lo conocen. */
  const etiquetasPorId = new Map();

  contadorInstancias += 1;
  const idBase = `connectors-${contadorInstancias}`;
  const idTitulo = `${idBase}-heading`;

  const seccion = document.createElement('section');
  seccion.className = 'connectors';
  seccion.setAttribute('aria-labelledby', idTitulo);

  const titulo = document.createElement('h2');
  titulo.id = idTitulo;
  titulo.className = 'connectors-heading';
  titulo.textContent = 'Conectores y ramas';

  const ayuda = document.createElement('p');
  ayuda.className = 'connectors-hint';
  ayuda.textContent =
    'La estructura mostrada aquí (orden y ramas) es la que define el programa, ' +
    'no la posición de los bloques en el área de trabajo. Usa el selector y el ' +
    'botón "Conectar aquí" para conectar un bloque; usa "Desconectar" para retirarlo.';

  const estructura = document.createElement('div');
  estructura.className = 'connectors-structure';

  const tituloSinConectar = document.createElement('h3');
  tituloSinConectar.className = 'connectors-subheading';
  tituloSinConectar.textContent = 'Bloques sin conectar';

  const listaSinConectar = document.createElement('ul');
  listaSinConectar.className = 'connectors-unconnected-list';

  const estado = document.createElement('p');
  estado.className = 'connectors-status';
  estado.setAttribute('role', 'status');
  estado.setAttribute('aria-live', 'polite');
  estado.setAttribute('aria-atomic', 'true');
  estado.textContent = 'Panel de conectores listo.';

  seccion.append(titulo, ayuda, estructura, tituloSinConectar, listaSinConectar, estado);

  /**
   * @param {string} mensaje
   * @param {{ aviso?: boolean }} [opcionesAnuncio]
   */
  function anunciar(mensaje, { aviso = false } = {}) {
    estado.textContent = mensaje;
    estado.classList.toggle('connectors-status-aviso', aviso);
  }

  /**
   * @param {string} contenedorId
   * @param {string} id
   * @param {number} nuevoIndice
   */
  function manejarMover(contenedorId, id, nuevoIndice) {
    try {
      programaActual = moverBloqueEnContenedor(programaActual, contenedorId, id, nuevoIndice);
      const etiqueta = etiquetasPorId.get(id) ?? 'bloque';
      renderizar();
      anunciar(`Bloque ${etiqueta} reordenado.`);
      if (notificarCambioPrograma) notificarCambioPrograma(programaActual);
    } catch (error) {
      anunciar(`No se pudo reordenar el bloque: ${/** @type {Error} */ (error).message}`, { aviso: true });
    }
  }

  /** @param {string} id */
  function manejarDesconectar(id) {
    try {
      const resultado = desconectarBloque(programaActual, id);
      programaActual = resultado.programa;
      const etiqueta = etiquetasPorId.get(id) ?? resultado.nodo.nodo;
      bloquesSinConectar = [...bloquesSinConectar, { nodo: resultado.nodo, etiqueta }];
      renderizar();
      anunciar(`Bloque ${etiqueta} desconectado; ahora está pendiente de conectar.`);
      if (notificarCambioPrograma) notificarCambioPrograma(programaActual);
    } catch (error) {
      anunciar(`No se pudo desconectar el bloque: ${/** @type {Error} */ (error).message}`, { aviso: true });
    }
  }

  /**
   * @param {NodoModelo} nodo
   * @param {string} etiqueta
   * @param {string} contenedorId
   */
  function manejarConectar(nodo, etiqueta, contenedorId) {
    try {
      programaActual = conectarBloque(programaActual, { contenedorId, nodo });
      bloquesSinConectar = bloquesSinConectar.filter((entrada) => entrada.nodo.id !== nodo.id);
      etiquetasPorId.set(nodo.id, etiqueta);
      renderizar();
      anunciar(`Bloque ${etiqueta} conectado.`);
      if (notificarCambioPrograma) notificarCambioPrograma(programaActual);
    } catch (error) {
      anunciar(`No se pudo conectar "${etiqueta}": ${/** @type {Error} */ (error).message}`, {
        aviso: true,
      });
    }
  }

  /**
   * Construye la lista anidada (semántica, no posicional) de una `Secuencia`
   * y, recursivamente, de las ramas/cuerpos que contenga.
   * @param {NodoModelo} secuencia
   * @returns {HTMLOListElement}
   */
  function construirListaSecuencia(secuencia) {
    const ol = document.createElement('ol');
    ol.className = 'connectors-sequence';

    const sentencias = /** @type {NodoModelo[]} */ (secuencia.sentencias);
    sentencias.forEach((sentencia, indice) => {
      const li = document.createElement('li');
      li.className = 'connectors-node';

      const encabezado = document.createElement('div');
      encabezado.className = 'connectors-node-header';

      const etiquetaSpan = document.createElement('span');
      etiquetaSpan.className = 'connectors-node-label';
      etiquetaSpan.textContent = `${etiquetasPorId.get(sentencia.id) ?? sentencia.nodo} (${sentencia.id})`;
      encabezado.append(etiquetaSpan);

      const acciones = document.createElement('div');
      acciones.className = 'connectors-node-actions';

      if (indice > 0) {
        const subir = document.createElement('button');
        subir.type = 'button';
        subir.className = 'connectors-action';
        subir.textContent = 'Mover arriba';
        subir.addEventListener('click', () => manejarMover(secuencia.id, sentencia.id, indice - 1));
        acciones.append(subir);
      }
      if (indice < sentencias.length - 1) {
        const bajar = document.createElement('button');
        bajar.type = 'button';
        bajar.className = 'connectors-action';
        bajar.textContent = 'Mover abajo';
        bajar.addEventListener('click', () => manejarMover(secuencia.id, sentencia.id, indice + 1));
        acciones.append(bajar);
      }

      const desconectar = document.createElement('button');
      desconectar.type = 'button';
      desconectar.className = 'connectors-action';
      desconectar.textContent = 'Desconectar';
      desconectar.addEventListener('click', () => manejarDesconectar(sentencia.id));
      acciones.append(desconectar);

      encabezado.append(acciones);
      li.append(encabezado);

      if (sentencia.nodo === TIPO_NODO.CONDICIONAL) {
        const ramas = document.createElement('div');
        ramas.className = 'connectors-branches';

        const ramaSi = document.createElement('section');
        ramaSi.className = 'connectors-branch';
        ramaSi.setAttribute('aria-label', `Rama verdadero de la condición ${sentencia.id}`);
        const tituloSi = document.createElement('h3');
        tituloSi.className = 'connectors-branch-heading';
        tituloSi.textContent = 'Verdadero';
        ramaSi.append(tituloSi, construirListaSecuencia(/** @type {NodoModelo} */ (sentencia.rama_si)));
        ramas.append(ramaSi);

        const ramaSino = /** @type {NodoModelo | null} */ (sentencia.rama_sino);
        if (ramaSino) {
          const ramaNo = document.createElement('section');
          ramaNo.className = 'connectors-branch';
          ramaNo.setAttribute('aria-label', `Rama falso de la condición ${sentencia.id}`);
          const tituloNo = document.createElement('h3');
          tituloNo.className = 'connectors-branch-heading';
          tituloNo.textContent = 'Falso';
          ramaNo.append(tituloNo, construirListaSecuencia(ramaSino));
          ramas.append(ramaNo);
        }

        li.append(ramas);
      } else if (NODOS_BUCLE.has(/** @type {string} */ (sentencia.nodo))) {
        const cuerpo = document.createElement('section');
        cuerpo.className = 'connectors-branch';
        cuerpo.setAttribute('aria-label', `Cuerpo del bucle ${sentencia.id}`);
        const tituloCuerpo = document.createElement('h3');
        tituloCuerpo.className = 'connectors-branch-heading';
        tituloCuerpo.textContent = 'Cuerpo del bucle';
        cuerpo.append(tituloCuerpo, construirListaSecuencia(/** @type {NodoModelo} */ (sentencia.cuerpo)));
        li.append(cuerpo);
      }

      ol.append(li);
    });

    return ol;
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {{ id: string, etiqueta: string }[]} contenedores
   */
  function poblarSelectContenedores(select, contenedores) {
    const optInicio = document.createElement('option');
    optInicio.value = ID_CONTENEDOR_INICIO;
    optInicio.textContent = 'Inicio (no admite conexiones entrantes)';
    select.append(optInicio);

    for (const contenedor of contenedores) {
      const opt = document.createElement('option');
      opt.value = contenedor.id;
      opt.textContent = contenedor.etiqueta;
      select.append(opt);
    }

    const optFin = document.createElement('option');
    optFin.value = ID_CONTENEDOR_FIN;
    optFin.textContent = 'Fin (no admite conexiones salientes)';
    select.append(optFin);

    if (contenedores.length > 0) {
      select.value = contenedores[0].id;
    }
  }

  /**
   * Reconstruye el contenido de la lista persistente `listaSinConectar` (el
   * propio elemento, ya insertado en el DOM al montar, nunca se sustituye
   * por otro: solo se reemplazan sus hijos, para que la referencia guardada
   * en el cierre siga siendo válida en cada `renderizar()` posterior).
   */
  function actualizarListaSinConectar() {
    const contenedores = listarContenedores(programaActual);
    const items = bloquesSinConectar.map((entrada, indice) => {
      const { nodo, etiqueta } = entrada;
      const li = document.createElement('li');
      li.className = 'connectors-unconnected-item';

      const texto = document.createElement('span');
      texto.className = 'connectors-unconnected-label';
      texto.textContent = `${etiqueta} (${nodo.id})`;

      const idSelect = `${idBase}-target-${indice}`;
      const label = document.createElement('label');
      label.className = 'connectors-target-label';
      label.setAttribute('for', idSelect);
      label.textContent = 'Conectar en:';

      const select = document.createElement('select');
      select.id = idSelect;
      select.className = 'connectors-target-select';
      poblarSelectContenedores(select, contenedores);

      const boton = document.createElement('button');
      boton.type = 'button';
      boton.className = 'connectors-action connectors-action-primary';
      boton.textContent = 'Conectar aquí';
      boton.addEventListener('click', () => manejarConectar(nodo, etiqueta, select.value));

      li.append(texto, label, select, boton);
      return li;
    });

    listaSinConectar.replaceChildren(...items);
  }

  function renderizar() {
    estructura.replaceChildren(construirListaSecuencia(/** @type {NodoModelo} */ (programaActual.cuerpo)));
    actualizarListaSinConectar();
  }

  // La primera vez, `listaSinConectar` ya está en el DOM (añadida en el
  // `append` de cabecera); `renderizar()` rellena su contenido con datos
  // reales, así que basta con llamarla una vez tras montar.
  renderizar();

  /**
   * @param {NodoModelo} nodo
   * @param {string} [etiqueta]
   */
  function agregarBloqueSinConectar(nodo, etiqueta) {
    if (!esNodo(nodo)) {
      throw new TypeError('"nodo" debe ser un NodoModelo válido');
    }
    if (buscarNodoPorId(programaActual, nodo.id)) {
      throw new ConexionInvalidaError(
        `el bloque '${nodo.id}' ya está conectado en el programa; no puede añadirse como pendiente`
      );
    }
    const etiquetaFinal = etiqueta && etiqueta.length > 0 ? etiqueta : nodo.nodo;
    bloquesSinConectar = [...bloquesSinConectar, { nodo, etiqueta: etiquetaFinal }];
    etiquetasPorId.set(nodo.id, etiquetaFinal);
    renderizar();
    anunciar(`Bloque ${etiquetaFinal} añadido como pendiente de conectar.`);
  }

  function destruir() {
    // Todos los listeners (botones de mover/desconectar/conectar) están
    // atados a nodos DOM reconstruidos en cada `renderizar()` mediante
    // `replaceChildren`/`replaceWith`: se descartan junto al propio nodo, sin
    // referencia colgante que limpiar aparte (mismo razonamiento que
    // `canvas-view.js`).
  }

  return {
    elemento: seccion,
    obtenerPrograma: () => programaActual,
    obtenerBloquesSinConectar: () => bloquesSinConectar.slice(),
    agregarBloqueSinConectar,
    destruir,
  };
}
